import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import SpDialog from "../../../globals/SpDialog";
import { getDocs } from "../../../globals/services";

const CsvImporter = ({ open, onClose, firebase, currentTab, refreshData }) => {
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parseError, setParseError] = useState("");
  const [importMode, setImportMode] = useState("new"); // "new" or "existing"
  const [selectedParent, setSelectedParent] = useState(null);
  const [availableParents, setAvailableParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [selectedSector, setSelectedSector] = useState(""); // For sectors tab
  const [preview, setPreview] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [step, setStep] = useState(1); // 1: upload, 2: review, 3: results
  const [groupedPreviewData, setGroupedPreviewData] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(false);

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      resetStates();
    }
  }, [open]);

  const fetchAvailableParents = useCallback(async () => {
    setLoadingParents(true);
    try {
      let result;
      switch (currentTab) {
        case "stacks":
          result = await getDocs("techStacks150r", false, 100, "name");
          break;
        case "sectors":
          // For sectors, we get industries from the selected sector
          if (selectedSector) {
            result = await getDocs(
              `sectors/${selectedSector}/industry`,
              false,
              100,
              "name"
            );
          } else {
            setAvailableParents([]);
            setLoadingParents(false);
            return;
          }
          break;
        case "categories":
          result = await getDocs("categories", false, 100, "name");
          break;
        default:
          setAvailableParents([]);
          setLoadingParents(false);
          return;
      }

      const parentsData = result.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        ref: doc.ref,
        data: doc.data(),
      }));

      setAvailableParents(parentsData);
    } catch (error) {
      console.error("Error fetching parents:", error);
      setAvailableParents([]);
    } finally {
      setLoadingParents(false);
    }
  }, [currentTab, selectedSector]);

  // Fetch available parents when import mode is "existing"
  useEffect(() => {
    if (importMode === "existing" && open) {
      fetchAvailableParents();
    }
  }, [importMode, open, currentTab, selectedSector, fetchAvailableParents]);

  const fetchAvailableSectors = useCallback(async () => {
    setLoadingSectors(true);
    try {
      const result = await getDocs("sectors", false, 100, "name");
      const sectorsData = result.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        ref: doc.ref,
        data: doc.data(),
      }));
      setAvailableSectors(sectorsData);
    } catch (error) {
      console.error("Error fetching sectors:", error);
      setAvailableSectors([]);
    } finally {
      setLoadingSectors(false);
    }
  }, []);

  // Fetch sectors when component opens for sectors tab
  useEffect(() => {
    if (open && currentTab === "sectors") {
      fetchAvailableSectors();
    }
  }, [open, currentTab, fetchAvailableSectors]);

  const resetStates = () => {
    setCsvData([]);
    setParseError("");
    setImportMode("new");
    setSelectedParent(null);
    setAvailableParents([]);
    setSelectedSector("");
    setAvailableSectors([]);
    setPreview([]);
    setImportResults(null);
    setLoading(false);
    setStep(1);
    setGroupedPreviewData({});
    setConflicts([]);
    setCheckingConflicts(false);
  };

  const groupDataByCategory = (data) => {
    const grouped = {};
    data.forEach((row, index) => {
      const category = row.Category?.trim();
      if (!category) return;

      if (!grouped[category]) {
        grouped[category] = {
          name: category,
          items: [],
          id: `category_${index}`,
        };
      }

      if (row.Subcategory?.trim()) {
        grouped[category].items.push({
          id: `item_${index}`,
          name: row.Subcategory.trim(),
          description: row.Description?.trim() || "",
          source: row.Source?.trim() || "",
          originalIndex: index,
        });
      }
    });

    return grouped;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      parseCSV(file);
    } else {
      setParseError("Please select a valid CSV file");
    }
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Handle escaped quotes
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        // Found delimiter outside quotes
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    // Add the last field
    result.push(current.trim());

    return result;
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;

        // Split into lines while preserving line breaks within quoted fields
        const lines = [];
        let currentLine = "";
        let inQuotes = false;

        for (let i = 0; i < csvText.length; i++) {
          const char = csvText[i];
          const nextChar = csvText[i + 1];

          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              // Escaped quote
              currentLine += '""';
              i++; // Skip next quote
            } else {
              // Toggle quote state
              inQuotes = !inQuotes;
              currentLine += char;
            }
          } else if ((char === "\n" || char === "\r") && !inQuotes) {
            // End of line outside quotes
            if (currentLine.trim()) {
              lines.push(currentLine.trim());
            }
            currentLine = "";

            // Skip \r\n combination
            if (char === "\r" && nextChar === "\n") {
              i++;
            }
          } else {
            currentLine += char;
          }
        }

        // Add the last line if it exists
        if (currentLine.trim()) {
          lines.push(currentLine.trim());
        }

        if (lines.length < 2) {
          setParseError(
            "CSV file must have at least a header and one data row"
          );
          return;
        }

        // Parse header
        const headers = parseCSVLine(lines[0]).map((header) =>
          header.replace(/^"|"$/g, "").trim()
        );

        // Validate required columns
        const requiredColumns = ["Category", "Subcategory"];
        const missingColumns = requiredColumns.filter(
          (col) => !headers.includes(col)
        );

        if (missingColumns.length > 0) {
          setParseError(
            `Missing required columns: ${missingColumns.join(", ")}`
          );
          return;
        }

        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          if (line) {
            const values = parseCSVLine(line).map((value) =>
              value.replace(/^"|"$/g, "").trim()
            );

            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = values[index] || "";
            });
            data.push(rowData);
          }
        }

        setCsvData(data);
        setPreview(data.slice(0, 5)); // Show first 5 rows for preview

        // Group data by category for better organization
        const grouped = groupDataByCategory(data);
        setGroupedPreviewData(grouped);
        setParseError("");

        // Automatically check for conflicts in the background
        setTimeout(() => {
          checkForConflictsBackground(grouped);
        }, 100);
      } catch (error) {
        setParseError("Error parsing CSV: " + error.message);
      }
    };

    reader.onerror = () => {
      setParseError("Error reading file");
    };

    reader.readAsText(file);
  };

  const getCollectionConfig = useCallback(() => {
    switch (currentTab) {
      case "stacks":
        return {
          mainCollection: "techStacks150r",
          subCollection: "technologies",
        };
      case "sectors":
        return {
          mainCollection: selectedSector
            ? `sectors/${selectedSector}/industry`
            : null,
          subCollection: "subSectors",
        };
      case "categories":
        return {
          mainCollection: "categories",
          subCollection: "subcollections",
        };
      default:
        return null;
    }
  }, [currentTab, selectedSector]);

  const checkForConflictsBackground = useCallback(
    async (groupedData) => {
      setCheckingConflicts(true);
      const conflictsList = [];
      const config = getCollectionConfig();

      if (!config?.mainCollection) {
        setCheckingConflicts(false);
        return;
      }

      try {
        // Always check main categories for conflicts (for both modes)
        const mainCollectionResult = await getDocs(
          config.mainCollection,
          false,
          1000,
          "name"
        );
        const existingMainItems = mainCollectionResult.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name.toLowerCase(),
          ref: doc.ref,
          data: doc.data(),
        }));

        for (const [categoryName] of Object.entries(groupedData)) {
          const existingMainItem = existingMainItems.find(
            (item) => item.name === categoryName.toLowerCase()
          );

          if (existingMainItem) {
            conflictsList.push({
              type: "category",
              categoryName,
              itemName: categoryName,
              existingItem: existingMainItem,
              action: "skip",
            });

            // Also check sub-items for this existing category
            try {
              const subCollectionPath = `${existingMainItem.ref.path}/${config.subCollection}`;
              const subItemsResult = await getDocs(
                subCollectionPath,
                false,
                1000,
                "name"
              );
              const existingSubItems = subItemsResult.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name.toLowerCase(),
                ref: doc.ref,
                data: doc.data(),
              }));

              for (const item of groupedData[categoryName].items) {
                const existingSubItem = existingSubItems.find(
                  (subItem) => subItem.name === item.name.toLowerCase()
                );

                if (existingSubItem) {
                  conflictsList.push({
                    type: "subitem",
                    categoryName,
                    itemName: item.name,
                    item,
                    existingItem: existingSubItem,
                    action: "skip",
                  });
                }
              }
            } catch (error) {
              console.error(
                `Error checking sub-items for ${categoryName}:`,
                error
              );
            }
          }
        }

        setConflicts(conflictsList);
      } catch (error) {
        console.error("Error checking for conflicts:", error);
        // Don't set parse error here since this is background checking
        console.warn("Background conflict check failed:", error.message);
      } finally {
        setCheckingConflicts(false);
      }
    },
    [getCollectionConfig]
  );

  // Re-check conflicts when import mode or sector changes (if we have data)
  useEffect(() => {
    if (Object.keys(groupedPreviewData).length > 0 && open) {
      // Small delay to avoid too many rapid checks
      const timeoutId = setTimeout(() => {
        checkForConflictsBackground(groupedPreviewData);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [
    importMode,
    selectedSector,
    selectedParent,
    checkForConflictsBackground,
    groupedPreviewData,
    open,
  ]);

  const checkForConflicts = async () => {
    setCheckingConflicts(true);
    const conflictsList = [];
    const config = getCollectionConfig();

    try {
      // Check main categories for conflicts
      if (importMode === "new") {
        const mainCollectionResult = await getDocs(
          config.mainCollection,
          false,
          1000,
          "name"
        );
        const existingMainItems = mainCollectionResult.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name.toLowerCase(),
          ref: doc.ref,
          data: doc.data(),
        }));

        for (const [categoryName] of Object.entries(groupedPreviewData)) {
          const existingItem = existingMainItems.find(
            (item) => item.name === categoryName.toLowerCase()
          );

          if (existingItem) {
            conflictsList.push({
              type: "category",
              categoryName,
              itemName: categoryName,
              existingItem,
              action: "skip",
            });
          }
        }
      }

      // Check sub-items for conflicts
      for (const [categoryName, categoryData] of Object.entries(
        groupedPreviewData
      )) {
        let parentRef = selectedParent?.ref;

        if (importMode === "new") {
          // For new mode, we need to check if category exists first
          const mainCollectionResult = await getDocs(
            config.mainCollection,
            false,
            1000,
            "name"
          );
          const existingMainItem = mainCollectionResult.docs.find(
            (doc) =>
              doc.data().name.toLowerCase() === categoryName.toLowerCase()
          );

          if (existingMainItem) {
            parentRef = existingMainItem.ref;
          } else {
            continue; // Skip if parent doesn't exist (will be created)
          }
        }

        if (parentRef) {
          try {
            const subCollectionPath = `${parentRef.path}/${config.subCollection}`;
            const subItemsResult = await getDocs(
              subCollectionPath,
              false,
              1000,
              "name"
            );
            const existingSubItems = subItemsResult.docs.map((doc) => ({
              id: doc.id,
              name: doc.data().name.toLowerCase(),
              ref: doc.ref,
              data: doc.data(),
            }));

            for (const item of categoryData.items) {
              const existingSubItem = existingSubItems.find(
                (subItem) => subItem.name === item.name.toLowerCase()
              );

              if (existingSubItem) {
                conflictsList.push({
                  type: "subitem",
                  categoryName,
                  itemName: item.name,
                  item,
                  existingItem: existingSubItem,
                  action: "skip",
                });
              }
            }
          } catch (error) {
            console.error(
              `Error checking sub-items for ${categoryName}:`,
              error
            );
          }
        }
      }

      setConflicts(conflictsList);
    } catch (error) {
      console.error("Error checking for conflicts:", error);
      setParseError("Error checking for existing items: " + error.message);
    } finally {
      setCheckingConflicts(false);
    }
  };

  const updateConflictAction = (conflictIndex, action) => {
    setConflicts((prev) =>
      prev.map((conflict, index) =>
        index === conflictIndex ? { ...conflict, action } : conflict
      )
    );
  };

  const proceedToReview = async () => {
    if (!csvData.length) {
      setParseError("No data to import");
      return;
    }

    // Validate sector selection for sectors tab
    if (currentTab === "sectors" && !selectedSector) {
      setParseError("Please select a sector type for import");
      return;
    }

    // Validate parent selection for existing mode
    if (importMode === "existing" && !selectedParent) {
      setParseError("Please select a parent item");
      return;
    }

    // Check for conflicts before proceeding
    await checkForConflicts();
    setStep(2);
  };

  const editItem = (categoryName, itemId, field, value) => {
    setGroupedPreviewData((prev) => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        items: prev[categoryName].items.map((item) =>
          item.id === itemId ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const removeItem = (categoryName, itemId) => {
    setGroupedPreviewData((prev) => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        items: prev[categoryName].items.filter((item) => item.id !== itemId),
      },
    }));
  };

  const removeCategory = (categoryName) => {
    setGroupedPreviewData((prev) => {
      const newData = { ...prev };
      delete newData[categoryName];
      return newData;
    });
  };

  const editCategoryName = (oldName, newName) => {
    if (oldName === newName) return;

    setGroupedPreviewData((prev) => {
      const newData = { ...prev };
      if (newData[oldName]) {
        newData[newName] = { ...newData[oldName], name: newName };
        delete newData[oldName];
      }
      return newData;
    });
  };

  const getTotalSubcategoriesCount = () => {
    return Object.values(groupedPreviewData).reduce(
      (total, cat) => total + cat.items.length,
      0
    );
  };

  const getCategorySubcategoryText = () => {
    const categoryCount = Object.keys(groupedPreviewData).length;
    const subcategoryCount = getTotalSubcategoriesCount();

    return `${categoryCount} ${
      categoryCount === 1 ? "category" : "categories"
    } with ${subcategoryCount} ${
      subcategoryCount === 1 ? "subcategory" : "subcategories"
    }`;
  };

  const createMainItem = async (categoryName, config) => {
    const mainItemData = {
      name: categoryName,
      title: categoryName,
      searchKeywords: categoryName.toLowerCase(),
      rank: 0,
      show: false,
      showRank: 0,
      description: "",
      logoStorage: "",
      addedOn: new Date(),
      assessments: [],
    };

    // For industry items, add specific fields
    if (currentTab === "sectors") {
      mainItemData.image = "";
    }

    // For categories, add specific fields
    if (currentTab === "categories") {
      mainItemData.iconImage = "";
    }

    try {
      const docRef = await firebase.createDocumentFromPath(
        config.mainCollection,
        mainItemData
      );
      return docRef;
    } catch (error) {
      console.error(`Error creating main item ${categoryName}:`, error);
      throw error;
    }
  };

  const createSubItem = async (subItemData, parentRef, config) => {
    const subData = {
      name: subItemData.Subcategory,
      title: subItemData.Subcategory,
      description: subItemData.Description || "",
      searchKeywords: subItemData.Subcategory.toLowerCase(),
      rank: 0,
      show: false,
      showRank: 0,
      logoStorage: "",
      addedOn: new Date(),
      assessments: [],
      source: subItemData.Source || "",
    };

    // For sectors sub-items, add specific fields
    if (currentTab === "sectors") {
      subData.image = "";
    }

    // For categories sub-items, add specific fields
    if (currentTab === "categories") {
      subData.iconImage = "";
    }

    try {
      const subcollectionPath = `${parentRef.path}/${config.subCollection}`;
      const docRef = await firebase.createDocumentFromPath(
        subcollectionPath,
        subData
      );
      return docRef;
    } catch (error) {
      console.error(
        `Error creating sub item ${subItemData.Subcategory}:`,
        error
      );
      throw error;
    }
  };

  const handleImport = async () => {
    const config = getCollectionConfig();
    if (!config || !config.mainCollection) {
      setParseError("Invalid collection configuration");
      return;
    }

    setLoading(true);
    const results = {
      success: 0,
      errors: [],
      created: {
        mainItems: [],
        subItems: [],
      },
    };

    try {
      for (const [categoryName, categoryData] of Object.entries(
        groupedPreviewData
      )) {
        // Skip categories with no items
        if (!categoryData.items.length) continue;

        // Check if this category should be skipped
        const categoryConflict = conflicts.find(
          (c) => c.type === "category" && c.categoryName === categoryName
        );

        if (categoryConflict?.action === "skip") {
          continue; // Skip this entire category
        }

        try {
          let parentRef = selectedParent?.ref;

          // Create main item if in "new" mode
          if (importMode === "new") {
            // Check if we should replace existing category
            if (
              categoryConflict?.action === "replace" &&
              categoryConflict.existingItem
            ) {
              // Delete existing category first (optional - or just update it)
              parentRef = categoryConflict.existingItem.ref;
            } else if (!categoryConflict) {
              // Create new category only if no conflict
              parentRef = await createMainItem(categoryName, config);
              results.created.mainItems.push(categoryName);
            } else {
              // Use existing category
              parentRef = categoryConflict.existingItem.ref;
            }
          }

          // Create sub-items
          for (const item of categoryData.items) {
            if (!item.name?.trim()) continue;

            // Check if this sub-item should be skipped
            const subItemConflict = conflicts.find(
              (c) =>
                c.type === "subitem" &&
                c.categoryName === categoryName &&
                c.itemName === item.name
            );

            if (subItemConflict?.action === "skip") {
              continue; // Skip this sub-item
            }

            const subItemData = {
              Subcategory: item.name,
              Description: item.description,
              Source: item.source,
            };

            try {
              if (
                subItemConflict?.action === "replace" &&
                subItemConflict.existingItem
              ) {
                // Replace existing sub-item
                await subItemConflict.existingItem.ref.update({
                  name: item.name,
                  title: item.name,
                  description: item.description,
                  source: item.source,
                  updatedOn: new Date(),
                });
              } else if (
                !subItemConflict ||
                subItemConflict?.action === "merge"
              ) {
                // Create new sub-item or merge (create if doesn't exist)
                await createSubItem(subItemData, parentRef, config);
              }

              results.created.subItems.push(item.name);
              results.success++;
            } catch (error) {
              results.errors.push(`Sub-item "${item.name}": ${error.message}`);
            }
          }
        } catch (error) {
          results.errors.push(`Category "${categoryName}": ${error.message}`);
        }
      }

      setImportResults(results);
      setStep(3);
      refreshData();
    } catch (error) {
      console.error("Import error:", error);
      setParseError("Import failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getSectorOptions = () => {
    return availableSectors.map((sector) => ({
      value: sector.id,
      label: sector.name,
    }));
  };

  return (
    <SpDialog open={open} onClose={onClose} useCustomBox={true}>
      <div className="csvImporter">
        <div className="csvImporter__header">
          <h2>
            Import from CSV -{" "}
            {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)}
          </h2>
          <p>Import categories and subcategories from a CSV file</p>
        </div>

        <div className="csvImporter__content">
          {/* Step 1: File Upload and Configuration */}
          {step === 1 && (
            <>
              {/* File Upload */}
              <div className="csvImporter__field">
                <label>Select CSV File:</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  disabled={loading}
                />
                <small>
                  Required columns: Category, Subcategory. Optional:
                  Description, Source
                </small>
              </div>

              {/* Sector Selection for sectors tab */}
              {currentTab === "sectors" && (
                <div className="csvImporter__field">
                  <label>Target Sector Type:</label>
                  {loadingSectors ? (
                    <div className="csvImporter__loading">
                      <CircularProgress size={20} />
                      <span>Loading sectors...</span>
                    </div>
                  ) : (
                    <select
                      value={selectedSector}
                      onChange={(e) => setSelectedSector(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Select sector type...</option>
                      {getSectorOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Import Mode */}
              {csvData.length > 0 && (
                <div className="csvImporter__field">
                  <label>Import Mode:</label>
                  <div className="csvImporter__radioGroup">
                    <label>
                      <input
                        type="radio"
                        value="new"
                        checked={importMode === "new"}
                        onChange={(e) => setImportMode(e.target.value)}
                        disabled={loading}
                      />
                      Create new main categories and subcategories
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="existing"
                        checked={importMode === "existing"}
                        onChange={(e) => setImportMode(e.target.value)}
                        disabled={loading}
                      />
                      Add subcategories to existing main category
                    </label>
                  </div>
                </div>
              )}

              {/* Parent Selection for existing mode */}
              {importMode === "existing" && (
                <div className="csvImporter__field">
                  <label>Select Parent Category:</label>
                  {loadingParents ? (
                    <div className="csvImporter__loading">
                      <CircularProgress size={20} />
                      <span>Loading categories...</span>
                    </div>
                  ) : (
                    <select
                      value={selectedParent?.id || ""}
                      onChange={(e) => {
                        const parent = availableParents.find(
                          (p) => p.id === e.target.value
                        );
                        setSelectedParent(parent);
                      }}
                      disabled={loading}
                    >
                      <option value="">Select parent category...</option>
                      {availableParents.map((parent) => (
                        <option key={parent.id} value={parent.id}>
                          {parent.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Error Display */}
              {parseError && (
                <div className="csvImporter__error">
                  <strong>Error:</strong> {parseError}
                </div>
              )}

              {/* Preview */}
              {preview.length > 0 && (
                <div className="csvImporter__preview">
                  <h4>Preview (first 5 rows):</h4>
                  <div className="csvImporter__table">
                    <table>
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Subcategory</th>
                          <th>Description</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, index) => (
                          <tr key={index}>
                            <td>{row.Category}</td>
                            <td>{row.Subcategory}</td>
                            <td>{row.Description}</td>
                            <td>{row.Source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p>
                    <strong>Total to import:</strong>{" "}
                    {getCategorySubcategoryText()}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Step 2: Review and Edit */}
          {step === 2 && (
            <div className="csvImporter__review">
              <h4>Review Import Data</h4>
              <p>
                Review and edit the data before importing. You can edit names,
                descriptions, or remove items.
              </p>

              {/* Summary */}
              <div className="csvImporter__summary">
                <h5>Import Summary:</h5>
                <p>
                  <span>
                    Categories to {importMode === "new" ? "create" : "use"}:
                  </span>
                  <span>{Object.keys(groupedPreviewData).length}</span>
                </p>
                <p>
                  <span>Total subcategories:</span>
                  <span>{getTotalSubcategoriesCount()}</span>
                </p>
                {importMode === "existing" && selectedParent && (
                  <p>
                    <span>Adding to:</span>
                    <span>{selectedParent.name}</span>
                  </p>
                )}
                {currentTab === "sectors" && selectedSector && (
                  <p>
                    <span>Sector type:</span>
                    <span>
                      {availableSectors.find(
                        (sector) => sector.id === selectedSector
                      )?.name || selectedSector}
                    </span>
                  </p>
                )}
              </div>

              {/* Conflicts Display */}
              {checkingConflicts && (
                <div className="csvImporter__loading">
                  <CircularProgress size={20} />
                  <span>Checking for existing items...</span>
                </div>
              )}

              {conflicts.length > 0 && (
                <div className="csvImporter__conflicts">
                  <h5>⚠️ Conflicts Found</h5>
                  <p>The following items already exist in the database:</p>
                  <div
                    style={{
                      marginBottom: "16px",
                      fontSize: "1.3rem",
                      color: "#6b7280",
                    }}
                  >
                    <strong>Action Options:</strong>
                    <ul style={{ marginTop: "4px", paddingLeft: "20px" }}>
                      <li>
                        <strong>Skip (Categories):</strong> Don't create/update
                        this category, but all sub-items will still be added to
                        the existing category
                      </li>
                      <li>
                        <strong>Replace (Categories):</strong> Update category
                        information with CSV data, and add all sub-items
                      </li>
                      <li>
                        <strong>Skip (Sub-items):</strong> Don't import this
                        specific sub-item (keep existing unchanged)
                      </li>
                      <li>
                        <strong>Replace (Sub-items):</strong> Overwrite existing
                        sub-item with new CSV data
                      </li>
                      <li>
                        <strong>Add to Category:</strong> Add new sub-items to
                        existing category (sub-items only)
                      </li>
                    </ul>
                  </div>
                  {conflicts.map((conflict, index) => (
                    <div key={index} className="csvImporter__conflicts__item">
                      <p>
                        <strong>
                          {conflict.type === "category"
                            ? "Category"
                            : "Subcategory"}
                          :
                        </strong>{" "}
                        {conflict.itemName}
                        {conflict.type === "category" && (
                          <>
                            <br />
                            <em
                              style={{ color: "#059669", fontSize: "1.2rem" }}
                            >
                              Note: Sub-items will still be added to this
                              existing category regardless of your choice
                            </em>
                          </>
                        )}
                        {conflict.type === "subitem" && (
                          <>
                            <br />
                            <em>in category: {conflict.categoryName}</em>
                          </>
                        )}
                      </p>
                      <div className="csvImporter__conflicts__item__actions">
                        <button
                          className="conflict-skip"
                          onClick={() => updateConflictAction(index, "skip")}
                          style={{
                            backgroundColor:
                              conflict.action === "skip"
                                ? "#374151"
                                : "#f3f4f6",
                            color:
                              conflict.action === "skip" ? "white" : "#374151",
                          }}
                        >
                          {conflict.type === "category"
                            ? "Skip Category"
                            : "Skip Sub-item"}
                        </button>
                        <button
                          className="conflict-replace"
                          onClick={() => updateConflictAction(index, "replace")}
                          style={{
                            backgroundColor:
                              conflict.action === "replace"
                                ? "#dc2626"
                                : "#fef2f2",
                            color:
                              conflict.action === "replace"
                                ? "white"
                                : "#dc2626",
                          }}
                        >
                          {conflict.type === "category"
                            ? "Update Category"
                            : "Replace Sub-item"}
                        </button>
                        {conflict.type === "subitem" && (
                          <button
                            className="conflict-merge"
                            onClick={() => updateConflictAction(index, "merge")}
                            style={{
                              backgroundColor:
                                conflict.action === "merge"
                                  ? "#059669"
                                  : "#f0fdf4",
                              color:
                                conflict.action === "merge"
                                  ? "white"
                                  : "#059669",
                            }}
                          >
                            Add to Category
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="csvImporter__reviewContent">
                {Object.entries(groupedPreviewData).map(
                  ([categoryName, categoryData]) => (
                    <div
                      key={categoryName}
                      className="csvImporter__categoryGroup"
                    >
                      <div className="csvImporter__categoryHeader">
                        <input
                          type="text"
                          value={categoryData.name}
                          onChange={(e) =>
                            editCategoryName(categoryName, e.target.value)
                          }
                          className="csvImporter__categoryNameInput"
                        />
                        <div className="csvImporter__categoryCount">
                          {categoryData.items.length}{" "}
                          {categoryData.items.length === 1 ? "item" : "items"}
                        </div>
                        <button
                          className="csvImporter__removeBtn"
                          onClick={() => removeCategory(categoryName)}
                          title="Remove entire category"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="csvImporter__itemsList">
                        {categoryData.items.map((item) => (
                          <div key={item.id} className="csvImporter__item">
                            <div className="csvImporter__itemField">
                              <label>Subcategory:</label>
                              <input
                                type="text"
                                value={item.name}
                                onChange={(e) =>
                                  editItem(
                                    categoryName,
                                    item.id,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="csvImporter__itemField">
                              <label>Description:</label>
                              <textarea
                                value={item.description}
                                onChange={(e) =>
                                  editItem(
                                    categoryName,
                                    item.id,
                                    "description",
                                    e.target.value
                                  )
                                }
                                rows="2"
                              />
                            </div>
                            <div className="csvImporter__itemField">
                              <label>Source:</label>
                              <input
                                type="text"
                                value={item.source}
                                onChange={(e) =>
                                  editItem(
                                    categoryName,
                                    item.id,
                                    "source",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <button
                              className="csvImporter__removeBtn csvImporter__removeBtn--small"
                              onClick={() => removeItem(categoryName, item.id)}
                              title="Remove this item"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Step 3: Import Results */}
          {step === 3 && importResults && (
            <div className="csvImporter__results">
              <h4>Import Results:</h4>
              <div className="csvImporter__results__summary">
                <p>
                  <strong>Successfully imported:</strong>{" "}
                  {importResults.success} items
                </p>
                {importResults.created.mainItems.length > 0 && (
                  <p>
                    <strong>Main categories created:</strong>{" "}
                    {importResults.created.mainItems.length}
                  </p>
                )}
                <p>
                  <strong>Subcategories created:</strong>{" "}
                  {importResults.created.subItems.length}
                </p>

                {importResults.errors.length > 0 && (
                  <div className="csvImporter__results__errors">
                    <strong>Errors:</strong>
                    <ul>
                      {importResults.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="csvImporter__actions">
          {/* Step 1 Actions */}
          {step === 1 && (
            <>
              <button
                className="spbtn spbtn-sub"
                onClick={proceedToReview}
                disabled={
                  loading ||
                  checkingConflicts ||
                  !csvData.length ||
                  (currentTab === "sectors" && !selectedSector) ||
                  (importMode === "existing" && !selectedParent)
                }
              >
                {checkingConflicts ? (
                  <div className="button-content">
                    <CircularProgress size={20} color="inherit" />
                    <span>Checking...</span>
                  </div>
                ) : (
                  "Review Data"
                )}
              </button>
              <button
                className="spbtn spbtn-canc"
                onClick={onClose}
                disabled={loading || checkingConflicts}
              >
                Cancel
              </button>
            </>
          )}

          {/* Step 2 Actions */}
          {step === 2 && (
            <>
              <button
                className="spbtn spbtn-sub"
                onClick={handleImport}
                disabled={
                  loading || Object.keys(groupedPreviewData).length === 0
                }
              >
                {loading ? (
                  <div className="button-content">
                    <CircularProgress size={20} color="inherit" />
                    <span>Importing...</span>
                  </div>
                ) : (
                  "Confirm Import"
                )}
              </button>
              <button
                className="spbtn spbtn-canc"
                onClick={() => setStep(1)}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="spbtn spbtn-canc"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}

          {/* Step 3 Actions */}
          {step === 3 && (
            <>
              <button
                className="spbtn spbtn-sub"
                onClick={() => {
                  resetStates();
                }}
              >
                Import More
              </button>
              <button className="spbtn spbtn-canc" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </SpDialog>
  );
};

export default CsvImporter;
